// apps/web/src/features/admin/transform-plugins/ExpressionEditor.tsx
//
// React wrapper around a CodeMirror 6 editor for DSL expressions.
// Mirrors the lifecycle pattern of FormulaEditor.tsx with the DSL-specific
// language helpers from expression-lang.ts.

'use client';

import { useEffect, useRef } from 'react';
import { EditorView, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap } from '@codemirror/commands';
import {
  expressionAutocomplete,
  EXPRESSION_EDITOR_CSS,
} from './expression-lang';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExpressionEditorProps {
  value: string;
  onChange: (next: string) => void;
  capabilities: string[];
  tenantFieldNames: string[];
  formSchemaKeys: string[];
  errors?: string[];
  readOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExpressionEditor({
  value,
  onChange,
  capabilities,
  tenantFieldNames,
  formSchemaKeys,
  errors,
  readOnly = false,
}: ExpressionEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  // Keep onChange in a ref so the CM listener always calls the latest version
  // without requiring a teardown + re-mount on every re-render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Create and destroy the editor. Re-mount when capabilities / tenantFieldNames
  // / formSchemaKeys change so autocomplete picks up the updated lists.
  // (State loss inside the editor on capability change is acceptable — these
  //  change rarely and the expression value is held in parent state.)
  useEffect(() => {
    if (!containerRef.current) return;

    const extensions = [
      keymap.of(defaultKeymap),
      expressionAutocomplete({ capabilities, tenantFieldNames, formSchemaKeys }),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onChangeRef.current(update.state.doc.toString());
        }
      }),
      EditorView.editable.of(!readOnly),
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // value intentionally omitted — synced via the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilities, tenantFieldNames, formSchemaKeys, readOnly]);

  // Inject CSS once on mount
  useEffect(() => {
    const id = 'expression-editor-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = EXPRESSION_EDITOR_CSS;
      document.head.appendChild(style);
    }
  }, []);

  // Sync external value changes into the editor (e.g. parent resets the value)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  return (
    <div className="expression-editor">
      {/* CodeMirror mount point */}
      <div ref={containerRef} />

      {/* Inline validation errors */}
      {errors && errors.length > 0 && (
        <div>
          {errors.map((err, i) => (
            <div
              key={i}
              className="rb-hint"
              style={{ color: '#dc2626', marginTop: 4 }}
            >
              {err}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
